import React, { useState } from 'react';
import { useNavigationEntries, useAppTranslation } from '@hooks';
import styled from '@emotion/styled';
import { colors, Icon, IconButton, Logo, Row, Space, Title } from 'common-components';
import { Popover } from 'antd';

import { ProfileDropdown } from './ProfileDropdown';
import { NavEntriesList } from './NavEntriesList';

const Container = styled(Row)`
  width: 100%;
  max-width: 1280px;
  height: 72px;
  margin: 0 auto;
  padding: 0 32px;
`;
const SettingsSection = styled.div``;

const LOGO_WIDTH = 82;
const LOGO_GAP = 16;

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 40px;
  padding: 32px 40px 32px 40px;
  background: ${colors.white};
  border: 1px solid ${colors.gray200};
  border-radius: 16px;
  box-shadow:
    0 20px 24px -4px rgba(16, 24, 40, 0.08),
    0 8px 8px -4px rgba(16, 24, 40, 0.03);
  box-sizing: border-box;
  width: 1235px;
  max-width: calc(100vw - 32px);
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
    padding: 1em;
  }
  @media (max-width: 600px) {
    grid-template-columns: repeat(1, 1fr);
    gap: 0.6em;
  }
`;

export const NavBar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const { translate } = useAppTranslation();

  const { currentEntry, defaultEntry, firstColumn, secondColumn, thirdColumn, fourthColumn } =
    useNavigationEntries();

  const selectedRoute = currentEntry?.title;

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const content = (
    <Wrapper>
      <NavEntriesList
        heading={translate('navigation.dropdownPanel.headings.analyser')}
        entries={firstColumn.entries}
        selectedRoute={selectedRoute}
        onEntryClick={closeMenu}
        iconColor={firstColumn.config.iconColor}
      />
      <NavEntriesList
        heading={translate('navigation.dropdownPanel.headings.mobiliser')}
        entries={secondColumn.entries}
        selectedRoute={selectedRoute}
        onEntryClick={closeMenu}
        iconColor={secondColumn.config.iconColor}
      />
      <NavEntriesList
        heading={translate('navigation.dropdownPanel.headings.launchpad')}
        entries={thirdColumn.entries}
        selectedRoute={selectedRoute}
        onEntryClick={closeMenu}
        iconColor={thirdColumn.config.iconColor}
      />
      <NavEntriesList
        heading={translate('navigation.dropdownPanel.headings.settings')}
        entries={fourthColumn.entries}
        selectedRoute={selectedRoute}
        onEntryClick={closeMenu}
        iconColor={fourthColumn.config.iconColor}
      />
    </Wrapper>
  );

  return (
    <Container justify={'space-between'} align={'middle'}>
      <Space size={LOGO_GAP} align={'center'}>
        <Logo preview={false} width={LOGO_WIDTH} />
        <Popover
          content={content}
          open={isMenuOpen}
          onOpenChange={setIsMenuOpen}
          placement="bottomLeft"
          arrow={false}
          align={{ offset: [-(LOGO_WIDTH + LOGO_GAP + 8), 8] }}
          styles={{ container: { padding: 0, background: 'transparent', boxShadow: 'none' } }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              gap: 8,
              padding: '8px 12px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <IconButton
                size="sm"
                icon={(currentEntry || defaultEntry).icon}
                iconProps={{
                  color: (currentEntry || defaultEntry).iconColor,
                  width: 24,
                  height: 24,
                }}
                style={{ borderColor: colors.gray100, padding: 7, boxShadow: 'none' }}
              />
              <Title level={5} style={{ color: colors.gray700 }}>
                {(currentEntry || defaultEntry).title}
              </Title>
            </div>
            <Icon icon={'ChevronDown'} color={colors.gray500} size={20} />
          </div>
        </Popover>
      </Space>
      <SettingsSection>
        <ProfileDropdown />
      </SettingsSection>
    </Container>
  );
};
